import { useState, useEffect, useCallback } from "react";
import {
  Header,
  Card,
  Text,
  CarInfo,
  Button,
  SelectCarPop,
} from "../../Components/Index";
import { useCars } from "../../Context/CarContext";
import {
  getVehicleServices,
  deleteService,
  updateService,
} from "../../services/api/serviceApi";
import { extractErrorMessage } from "../../services/api/errorHandler";
import {
  ServiceCard,
  EditServicePopup,
  DeleteServicePopup,
  LoadingState,
  ErrorState,
  EmptyState,
  getMechanicName,
  toISODateInput,
  isValidISODate,
  sortServicesDesc,
  toLatinDigits,
  toPersianNumericDisplay,
  parsePersianNumber,
} from "./components";
import WorkerRecentServicesView from "./components/WorkerRecentServicesView";

function CustomerLatestServices() {
  const { getSelectedCar, selectCar } = useCars();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSelectCarPopOpen, setIsSelectCarPopOpen] = useState(false);

  // collapsed by default: empty set => all collapsed
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [editingService, setEditingService] = useState(null);
  const [deletingService, setDeletingService] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState("");

  // edit form state (ISO date internally, Jalali in UI)
  const [editMileage, setEditMileage] = useState("");
  const [editDateISO, setEditDateISO] = useState("");
  const [editCost, setEditCost] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editMechanic, setEditMechanic] = useState("");

  const selectedCar = getSelectedCar();

  const loadServices = useCallback(async () => {
    if (!selectedCar) {
      setServices([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getVehicleServices(selectedCar.id);
      if (response.success && response.data) {
        const allServices = [
          ...(response.data.periodicKmTime || []).map((s) => ({
            ...s,
            fallbackType: "دوره‌ای (کیلومتر/زمان)",
          })),
          ...(response.data.actionTime || []).map((s) => ({
            ...s,
            fallbackType: "دوره‌ای (عملکرد)",
          })),
          ...(response.data.repair || []).map((s) => ({
            ...s,
            fallbackType: "تعویض / تعمیر",
          })),
        ];

        const enriched = allServices.map((s) => ({
          ...s,
          displayType: s.category?.title || s.fallbackType,
        }));

        setServices(sortServicesDesc(enriched));
      } else {
        setError(response.message || "خطا در دریافت سرویس‌ها");
        setServices([]);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "خطای شبکه";
      setError(msg);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCar]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // reset expansion/popup state when car changes so new data starts collapsed
  useEffect(() => {
    setExpandedIds(new Set());
    setEditingService(null);
    setDeletingService(null);
    setActionError("");
  }, [selectedCar?.id]);

  // lock body scroll while a popup is open
  useEffect(() => {
    const anyOpen = editingService !== null || deletingService !== null;
    if (!anyOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [editingService, deletingService]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleOpenSelectCarPop = () => setIsSelectCarPopOpen(true);
  const handleCloseSelectCarPop = () => setIsSelectCarPopOpen(false);
  const handleConfirmCarSelection = (carId) => {
    selectCar(carId);
    handleCloseSelectCarPop();
  };

  const startEdit = (service) => {
    setDeletingService(null);
    setActionError("");
    setEditingService(service);
    // Display mileage/cost with Persian digits + thousands separators
    // regardless of backend format (e.g. 1234567 -> "۱٬۲۳۴٬۵۶۷").
    setEditMileage(
      service.mileage === null || service.mileage === undefined
        ? ""
        : toPersianNumericDisplay(String(service.mileage))
    );
    setEditDateISO(toISODateInput(service.serviceDate));
    setEditCost(
      service.cost === null || service.cost === undefined || service.cost === ""
        ? ""
        : toPersianNumericDisplay(String(service.cost))
    );
    setEditDesc(service.description || "");
    const mech = getMechanicName(service);
    setEditMechanic(mech === "—" ? "" : mech);
  };

  const cancelEdit = () => {
    setEditingService(null);
    setActionError("");
  };

  const cancelDelete = () => {
    setDeletingService(null);
    setActionError("");
  };

  const saveEdit = async () => {
    if (!editingService) return;
    // Accept Persian/Arabic/Latin digits; display layer keeps Persian digits,
    // backend always receives plain Latin numbers.
    const mileageNum = parsePersianNumber(editMileage);
    if (!Number.isFinite(mileageNum) || !editDateISO) {
      setActionError("کیلومتر و تاریخ سرویس الزامی است");
      return;
    }
    if (!isValidISODate(editDateISO)) {
      setActionError("تاریخ سرویس معتبر نیست");
      return;
    }
    setActionPending(true);
    setActionError("");
    try {
      const baseDetails =
        editingService.details && typeof editingService.details === "object" && !Array.isArray(editingService.details)
          ? { ...editingService.details }
          : {};
      const mechanicTrim = editMechanic.trim();
      if (mechanicTrim) baseDetails.mechanicName = mechanicTrim;
      else delete baseDetails.mechanicName;

      // Backend expects Gregorian ISO "YYYY-MM-DD" — ServiceDatePicker already
      // converts the selected Jalali day to ISO, so the API contract is unchanged.
      // mileage/cost are converted from Persian display digits to Latin numbers.
      const payload = {
        mileage: mileageNum,
        serviceDate: editDateISO,
        description: editDesc || "",
        details: baseDetails,
      };
      if (toLatinDigits(editCost).replace(/\D/g, "") !== "")
        payload.cost = parsePersianNumber(editCost);
      else payload.cost = null;

      const res = await updateService(editingService.id, payload);
      if (res && res.success === false) {
        setActionError(res.message || "خطا در ویرایش سرویس");
        return;
      }
      setEditingService(null);
      await loadServices(); // refresh from real backend state
    } catch (err) {
      setActionError(extractErrorMessage(err, "خطا در ویرایش سرویس"));
    } finally {
      setActionPending(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingService) return;
    setActionPending(true);
    setActionError("");
    try {
      const res = await deleteService(deletingService.id);
      if (res && res.success === false) {
        setActionError(res.message || "خطا در حذف سرویس");
        return;
      }
      const removedId = deletingService.id;
      setDeletingService(null);
      setEditingService(null);
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(removedId);
        return next;
      });
      await loadServices(); // refresh from real backend state
    } catch (err) {
      setActionError(extractErrorMessage(err, "خطا در حذف سرویس"));
    } finally {
      setActionPending(false);
    }
  };

  const showList = !loading && !error && services.length > 0;
  const showEmpty = !loading && !error && services.length === 0;

  return (
    <div className="ls-page">
      <Header Text="آخرین سرویس‌ها" hideR="" LinkR="/" />

      {selectedCar ? (
        <CarInfo
          Src={selectedCar.Src}
          Plate1={selectedCar.Plate1}
          Plate2={selectedCar.Plate2}
          Plate3={selectedCar.Plate3}
          End={selectedCar.End}
          Km={selectedCar.Km}
          CarModel={selectedCar.CarModel}
          ProductionYear={selectedCar.ProductionYear}
          CarColor={selectedCar.CarColor}
          onSelect={handleOpenSelectCarPop}
          hideSelectionUI
        />
      ) : (
        <Card className="ls-no-car">
          <Text FontWight="Bold" position="right">
            خودرویی انتخاب نشده است
          </Text>
          <Button onClick={handleOpenSelectCarPop}>انتخاب خودرو</Button>
        </Card>
      )}

      {(loading || (error && !loading) || showEmpty) && (
        <div className="ls-status">
          {loading && <LoadingState />}
          {error && !loading && <ErrorState message={error} onRetry={loadServices} />}
          {showEmpty && <EmptyState />}
        </div>
      )}

      {showList && (
        <div className="latest-services-scroll">
          <div className="completed-list">
            {services.map((service) => (
              <ServiceCard
                key={`svc-${service.id}`}
                service={service}
                isOpen={expandedIds.has(service.id)}
                onToggle={() => toggleExpand(service.id)}
                onEdit={() => startEdit(service)}
                onDelete={() => {
                  setEditingService(null);
                  setActionError("");
                  setDeletingService(service);
                }}
              />
            ))}
          </div>
        </div>
      )}

      <EditServicePopup
        service={editingService}
        mileage={editMileage}
        onMileageChange={(e) => setEditMileage(toPersianNumericDisplay(e.target.value))}
        dateISO={editDateISO}
        onDateChange={setEditDateISO}
        cost={editCost}
        onCostChange={(e) => setEditCost(toPersianNumericDisplay(e.target.value))}
        mechanic={editMechanic}
        onMechanicChange={(e) => setEditMechanic(e.target.value)}
        description={editDesc}
        onDescriptionChange={(e) => setEditDesc(e.target.value)}
        pending={actionPending}
        error={actionError}
        onCancel={cancelEdit}
        onSave={saveEdit}
      />

      <DeleteServicePopup
        service={deletingService}
        pending={actionPending}
        error={actionError}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />

      <SelectCarPop
        isOpen={isSelectCarPopOpen}
        onClose={handleCloseSelectCarPop}
        onConfirm={handleConfirmCarSelection}
      />
    </div>
  );
}

function isServiceWorkerView() {
  try {
    const sessionSection = sessionStorage.getItem("tasmeh:lastSection");
    if (sessionSection) return sessionSection === "serviceWorker";
    return localStorage.getItem("tasmeh:isMechanic") === "true" || localStorage.getItem("tasmeh:lastSection") === "serviceWorker";
  } catch {
    return false;
  }
}

function LatestServices() {
  return isServiceWorkerView() ? <WorkerRecentServicesView /> : <CustomerLatestServices />;
}

export default LatestServices;
